import { Feature } from 'ol'
import { Geometry } from 'ol/geom'
import * as olProj from 'ol/proj'
import { useAsyncState } from '@vueuse/core'
import GeoJSON from 'ol/format/GeoJSON'
import Style from 'ol/style/Style'
import { Extent } from 'ol/extent'
import { useGeoDataStore } from '~/stores/geoData'
import { GeoDataType, RegionType } from '~/types/types'

type VectorOptions = {
  generalStyle?: Style
  styleParser?: (feature: Feature<Geometry>, index: number) => Style
}

export function useGeojsonDataFetch(
  contentType: GeoDataType,
  { generalStyle, styleParser }: VectorOptions = {}
) {
  const geoDataStore = useGeoDataStore()
  const geoJson = new GeoJSON({
    dataProjection: 'EPSG:3006',
    featureProjection: 'EPSG:3857',
  })
  const dataPoint = computed(() => {
    let dataPoint = null
    if (contentType === 'electricity-network-owners') dataPoint = geoDataStore.electricityGridOwners
    if (contentType === 'municipalities') dataPoint = geoDataStore.municipalities
    if (contentType === 'counties') dataPoint = geoDataStore.counties
    if (!dataPoint) return []
    return dataPoint.map((data) => {
      const feature = geoJson.readFeature(data.geojson)

      if (data.center_east && data.center_north) {
        const centerCoordinates = [data.center_east, data.center_north]
        const src = 'EPSG:3006'
        const dest = 'EPSG:3857'
        const transformedCoordinates = olProj.transform(centerCoordinates, src, dest)

        feature.setProperties({
          center_east: transformedCoordinates[0],
          center_north: transformedCoordinates[1],
          slug: data.slug,
        })
      }
      return feature
    })
  })
  const isReady = ref(false)
  const isLoading = ref(true)
  const initData = async () => {
    if (contentType === 'electricity-network-owners') {
      if (geoDataStore.electricityGridOwners.length === 0) {
        await geoDataStore.initElectricityGridOwners()
      }
    }

    if (contentType === 'municipalities') {
      if (geoDataStore.municipalities.length === 0) {
        await geoDataStore.initMunicipalities()
      }
    }

    if (contentType === 'counties') {
      if (geoDataStore.counties.length === 0) {
        await geoDataStore.initCounties()
      }
    }
    isLoading.value = false
    isReady.value = true
  }
  initData()
  return { features: dataPoint, isReady, isLoading }
}
export function useRegionExtents(regionType: RegionType | undefined, regionId: string | undefined) {
  const extent = ref<Extent | null>(null)
  if (!regionType || !regionId) return { extent, isReady: ref(false), isLoading: ref(false) }

  const contentType = {
    municipality: 'municipalities',
    county: 'counties',
    'electricity-grid': 'electricity-network-owners',
  }[regionType] as GeoDataType

  const { features, isReady, isLoading } = useGeojsonDataFetch(contentType)

  const propertyKey = {
    municipality: 'municipality_code',
    county: 'county_code',
    'electricity-grid': 'koncession',
  }[regionType]

  watch(
    features,
    () => {
      if (isReady.value) {
        const region = features.value.find((feature) => feature.get(propertyKey || '') === regionId)
        if (region) {
          extent.value = region.getGeometry()?.getExtent() || null
        }
      }
    },
    { immediate: true }
  )
  return { extent, isReady, isLoading }
}
